import React from "react";
import { cn } from "@/lib/utils";
import { providerMeta } from "@/pages/vb/run/providerMeta";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";
export type AvatarKind = "user" | "company" | "workspace";

type Props = {
  size?: AvatarSize;
  className?: string;
  /** AI provider (OPENAI / GEMINI / PERPLEXITY / CLAUDE / …). Renders the
   *  provider-coloured tile + glyph. Mutually exclusive with `name`. */
  model?: string;
  /** Initials source for a person/company/workspace monogram. */
  name?: string;
  /** Monogram shape/tone. `user` is round; company/workspace are squircles. */
  kind?: AvatarKind;
  /** Image URL — rendered in place of initials. */
  src?: string;
  /** Force the round shape regardless of kind. */
  round?: boolean;
  ariaLabel?: string;
  /** Decorative — a visible label sits beside it, so hide it from SR. */
  decorative?: boolean;
};

/** Two initials: first+last for multi-word names, the first two characters
 *  otherwise. The single-word case used to yield ONE letter here while the
 *  sidebar chip and DomainFavicon both yielded two, so the same person rendered
 *  "D" in settings and "DE" in the rail. Two is the majority behaviour and the
 *  commoner avatar convention, so the other two won. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** One avatar for every mark in the app: AI-model provider tiles (folding the
 *  four hand-rolled model marks) and user/workspace/company monograms or photos.
 *  Model marks colour by provider; monograms are round (people) or squircle
 *  (company/workspace). */
export function Avatar({
  size = "md",
  className,
  model,
  name,
  kind = "company",
  src,
  round,
  ariaLabel,
  decorative,
}: Props) {
  // Decorative marks (a visible label sits beside them) are hidden from SR;
  // standalone marks expose role="img" + an accessible name.
  const a11y = (label: string | undefined) =>
    decorative
      ? ({ "aria-hidden": true } as const)
      : ({ role: "img" as const, "aria-label": ariaLabel ?? label });

  if (model != null) {
    const meta = providerMeta(model);
    return (
      <span
        {...a11y(meta.label)}
        className={cn(
          "vb-avatar",
          `vb-avatar--${size}`,
          `vb-avatar--${meta.className}`,
          round && "vb-avatar--round",
          className,
        )}
      >
        {meta.logo}
      </span>
    );
  }

  const isRound = round ?? kind === "user";
  return (
    <span
      {...a11y(name)}
      className={cn(
        "vb-avatar",
        `vb-avatar--${size}`,
        `vb-avatar--${kind}`,
        isRound && "vb-avatar--round",
        className,
      )}
    >
      {src ? <img src={src} alt="" /> : initials(name ?? "")}
    </span>
  );
}

export default Avatar;
